import { matchValueFor } from '../game/Chain.js';
import type { GameStateData } from '../game/GameState.js';
import { isPip, tileEquals, type Chain, type Pip, type Tile } from '../game/types.js';

/**
 * Pure feature helpers for `KozyolEvaluator`. Kept apart from the evaluator
 * so the weighting stays readable and each helper is testable on its own.
 */

/**
 * Open pip of every active end, NOT deduplicated: a line whose two ends both
 * show 6 is blocked twice over, which matters to the blocking feature.
 * Shared arm objects (an uncovered double lead) still count as two ends.
 */
export function chainEndPips(chain: Chain): readonly Pip[] {
  const out: Pip[] = [];
  for (const idx of chain.activeArms) {
    const arm = chain.arms[idx];
    if (!arm) continue;
    const v = matchValueFor(arm);
    if (isPip(v)) out.push(v);
  }
  return out;
}

/** `hand` minus one copy of `tile`. */
export function handWithout(hand: readonly Tile[], tile: Tile): readonly Tile[] {
  const idx = hand.findIndex((t) => tileEquals(t, tile));
  if (idx < 0) return hand;
  return [...hand.slice(0, idx), ...hand.slice(idx + 1)];
}

/** Number of distinct pip values present in `hand`. */
export function distinctSuits(hand: readonly Tile[]): number {
  const seen = new Set<Pip>();
  for (const t of hand) {
    seen.add(t[0]);
    seen.add(t[1]);
  }
  return seen.size;
}

/** How many of `ends` the hand could still follow on its next turn. */
export function coveredEnds(ends: readonly Pip[], hand: readonly Tile[]): number {
  let covered = 0;
  for (const pip of ends) {
    if (hand.some((t) => t[0] === pip || t[1] === pip)) covered += 1;
  }
  return covered;
}

/** Partner seat in a 2v2 table, or -1 when `seat` has none. */
export function partnerOf(state: GameStateData, seat: number): number {
  const team = state.teamMap.teamOf[seat];
  return state.teamMap.teamOf.findIndex((t, i) => t === team && i !== seat);
}
